import type { SpreadsheetAPI } from '@carboncopyco/editor-ui';
import {
  type FindReplaceRule,
  type GroupSchemaType,
  type GuidanceTooltipSchemaType,
  IActionType,
  type ICustomTemplateChart,
  type IDynamicEditData,
  type IEditActionsType,
  type IEditorDraft,
  type IEditorStatus,
  type IFindAndReplaceRuleType,
  type IOrganizationData,
  type ISnippetRule,
  type IUser,
  type IVariable,
  type Modification,
  type NavigationEditSchemaType,
  type Transaction as NetworkTransaction,
  type RecordingType,
  type SchemaType,
  type SchemaTypeToContent,
  type Selector,
  type TableCraftFunction,
  type TableCraftObject,
  type TableCraftRow,
  type EditorPerformanceResult,
} from '@carboncopyco/types';

export interface APIEndpoints extends SpreadsheetAPI {
  uploadImage?: (fileData: File) => Promise<{ link_to_file?: string }>;
  uploadCodeSnippet?: (snippet: File) => Promise<{ link_to_file?: string }>;
  uploadTransactionResponseContent?: (
    responseContent: File
  ) => Promise<{ link_to_file?: string }>;
  uploadTransactionRepresentationFields?: (
    representationFields: File
  ) => Promise<{ link_to_file?: string }>;
  getChartTemplates?: () => Promise<ICustomTemplateChart[]>;
  updateChartTemplate?: ({
    chartId,
    payload,
  }: {
    chartId: string;
    payload: ICustomTemplateChart;
  }) => Promise<void>;
  createChartTemplate?: (
    payload: ICustomTemplateChart
  ) => Promise<ICustomTemplateChart>;
  deleteChartTemplate?: (
    chartTemplateId: string
  ) => Promise<ICustomTemplateChart>;
  recorderSessionRefresh?: (args: {
    recorderUuid: string;
    sessionId: string;
  }) => Promise<void>;
  openUrlLink?: (url: string) => void;
  createModification?: <T extends SchemaType = any>({
    schemaType,
    selectors,
    content,
    isLocked,
    isActive,
    groupId,
  }: {
    schemaType: T;
    selectors?: Selector[];
    content: SchemaTypeToContent<T>;
    isLocked?: boolean;
    isActive?: boolean;
    groupId?: string;
  }) => Promise<Modification>;
  updateModification?: <T extends SchemaType = any>({
    modificationId,
    isActive,
    isLocked,
    order,
    selectors,
    content,
  }: {
    modificationId: Modification['modificationId'];
    isActive?: boolean;
    isLocked?: boolean;
    order?: number;
    selectors?: Selector[];
    content?: SchemaTypeToContent<T>;
  }) => Promise<Modification>;
  deleteModification?: ({
    modificationId,
  }: {
    modificationId: Modification['modificationId'];
  }) => Promise<Modification>;
  reorderModification?: ({
    id,
    afterId,
  }: {
    id: Modification['modificationId'];
    afterId: Modification['modificationId'];
  }) => Promise<unknown>;
  searchTransactions?: ({
    htmlElementString,
    referer,
  }: {
    htmlElementString: string;
    referer?: string;
  }) => Promise<{
    total: number;
    results: NetworkTransaction[];
    searchString: string;
  }>;
  updateTableCraftRow?: (data: {
    objectId: string;
    row_id: string;
    column: string;
    value: string | boolean;
    id_column: string;
    enrich: boolean;
  }) => Promise<void>;
  updateTableCraftColumn?: (data: {
    objectId: string;
    col_name: string;
    generator: string;
    generator_args?: Record<string, unknown> | string;
    enrich: boolean;
  }) => Promise<void>;
  updateTableCraftSettings?: (settings: {
    objectId: string;
    hide: boolean;
    display_name: string;
    filter_columns: string[];
    set_object_pk: string;
  }) => Promise<void>;
  revertTableCraftModification?: (data: {
    objectId: string;
    entity: 'cell' | 'column' | 'row';
    row_id: string;
    col_name: string;
    enrich: boolean;
  }) => Promise<void>;
  getTableCraftObjects?: (queryParams?: {
    searchQuery?: string;
    page?: number;
    size?: number;
  }) => Promise<TableCraftObject[] | { status: 'indexing' }>;
  getTableCraftRows?: (
    objectId: string,
    queryParams?: {
      searchQuery?: string;
      page?: number;
      size?: number;
    }
  ) => Promise<TableCraftRow[]>;
  getTableCraftFunctions?: () => Promise<{
    functions: TableCraftFunction[];
  }>;
  getTableCraftEnrichmentColumns?: ({
    objectId,
    col_name,
  }: {
    objectId: string;
    col_name: string;
  }) => Promise<string[]>;
  visualTableCraftSearch?: ({
    htmlElementString,
    referer,
  }: {
    htmlElementString: string;
    referer?: string;
  }) => Promise<TableCraftObject[]>;
  aiAssist?: (data: {
    task_type: AssistTypes;
    prompt: string;
    assist_data?: Record<string, any>;
  }) => Promise<{
    tokens: number;
    data: Record<string, string> | Record<string, string>[];
  }>;
}

export interface Action {
  type: IActionType;
  payload?: any;
  status?: IEditorStatus;
  origin?: string;
  senderId?: string;
}

export type EditorMode = 'editor' | 'preview';

export interface StartPayload {
  edits: IDynamicEditData[];
  chartsTemplates?: ICustomTemplateChart[];
  variables: IVariable[];
  rules: FindReplaceRule[];
  modifications: Modification[];
  origin?: string;
  flags: Record<string, any>;
  mode: EditorMode;
  recordingType: RecordingType;
  filesServiceDomain: string;
  presentationDomain: string;
}

export interface Metadata {
  favicon?: string;
  faviconBackups?: string[];
  title?: string;
  url?: string;
  location?: {
    encodedUrl: string;
    decodedUrl: string;
  };
  recorderSessionId?: string;
}

export interface Transaction extends NetworkTransaction {
  id: string;
}

export type FindAndReplaceMode =
  | {
      ruleType: IFindAndReplaceRuleType;
    }
  | {
      ruleType: IFindAndReplaceRuleType.replaceImageWithVariable;
      variableId?: IVariable['variableId'];
    };

export interface EditorSdkEvents {
  loaded: boolean;
  updatedRecordingType: RecordingType;
  updateMetadata: Metadata;
  updatedDraft: IEditorDraft;
  updatedInspectAction: IEditActionsType;
  updatedFindAndReplaceRules: FindReplaceRule[];
  updatedCodeSnippets: ISnippetRule[];
  updatedVariables: IVariable[];
  updatedModifications: Modification[];
  updatedTableCraftObjects: {
    data: TableCraftObject[];
    status: 'loading' | 'indexing' | 'success' | 'error';
  };
  updatedTableCraftRows: {
    data: TableCraftRow[];
    status: 'loading' | 'success';
  };
  updatedTableCraftFunctions: {
    data: TableCraftFunction[];
    status: 'loading' | 'success';
  };
  updatedEdits: IDynamicEditData[];
  updatedSpotEditsModifications: Modification<'edit'>[];
  updatedRedos: IDynamicEditData[];
  updatedChartTemplates: ICustomTemplateChart[];
  startFindAndReplaceMode: FindAndReplaceMode;
  stopFindAndReplaceMode: undefined;
  startBlockInteractionsMode: undefined;
  stopBlockInteractionsMode: undefined;
  startGuidanceTooltipsMode: undefined;
  stopGuidanceTooltipsMode: undefined;
  showBackdropOverlay: boolean;
  updateTransactions: Transaction[];
  updatedTransactionModifications: Modification<'transaction_rule'>[];
  updatedNavigationModifications: Modification<NavigationEditSchemaType.blockInteractions>[];
  onEditorLoaded: undefined;
  openDataRuleCreation: Transaction[];
  updatedGuidanceTooltipsModifications: Modification<GuidanceTooltipSchemaType.guidanceTooltip>[];
  editGuidanceModification: Modification<GuidanceTooltipSchemaType.guidanceTooltip>;
  openVisualSearch: {
    htmlElementString: string;
    referer?: string;
    results: TableCraftObject[];
  };
  updatedBlockInteractionsSettings: Modification<NavigationEditSchemaType.blockInteractionsSettings>;
  isGuidanceTooltipsEditLoading: boolean;
  isGuidanceTooltipOpen: boolean;
  updatedAiPromptGroup: Modification<GroupSchemaType.aiPromptGroup>[];
  performanceResults: EditorPerformanceResult;
  isPerformanceMeasuringStarted: boolean;
  startPerformancePickSelectorMode: undefined;
  updatedPerformanceSelectorXpath: string;
}

export interface EditorEngineMessages {
  [IActionType.updateRuleGlobalStatus]: {
    id: string;
    isGlobal: boolean;
  };
  [IActionType.startup]: StartPayload;
  [IActionType.startPreview]: StartPayload;
  [IActionType.setEditMode]: IEditActionsType;
  [IActionType.closeToast]: undefined;
  [IActionType.undoElement]: IDynamicEditData;
  [IActionType.redoElement]: IDynamicEditData;
  [IActionType.setChartsTemplates]: ICustomTemplateChart[];
  [IActionType.setVariables]: IVariable[];
  [IActionType.setUserData]: IUser & {
    organizationId: string;
    organization: IOrganizationData;
  };
  [IActionType.setPresentationDomain]: {
    presentationDomain: string;
  };
  [IActionType.createRuleResponse]: {
    status: 'success' | 'error';
  };
  [IActionType.startFindAndReplaceMode]: FindAndReplaceMode;
  [IActionType.stopFindAndReplaceMode]: undefined;
  [IActionType.startBlockInteractionsMode]: undefined;
  [IActionType.startGuidanceTooltipsMode]: undefined;
  [IActionType.stopGuidanceTooltipsMode]: undefined;
  [IActionType.stopBlockInteractionsMode]: undefined;
  [IActionType.getMetadata]: undefined;
  [IActionType.reloadEditorFrame]: undefined;
  [IActionType.setRules]: FindReplaceRule[];
  [IActionType.notify]: {
    type: 'dark' | 'error' | 'warning' | 'info' | 'success';
    message: string;
  };
  [IActionType.setModifications]: Modification[];
  [IActionType.highlightTarget]: IDynamicEditData;
  [IActionType.destroyHighlightTarget]: IDynamicEditData;
  [IActionType.navigateToTarget]: IDynamicEditData;
  [IActionType.updateUrl]: { url: string };
  [IActionType.createDataRuleFromElement]: CreateDataRuleFromElementPayload;
  [IActionType.restoreGuidanceModification]: IDynamicEditData;
  [IActionType.highlightGuidanceTooltip]: Modification<GuidanceTooltipSchemaType.guidanceTooltip>;
  [IActionType.destroyHighlightGuidanceTooltip]: Modification<GuidanceTooltipSchemaType.guidanceTooltip>;
  [IActionType.navigateToModification]: Modification<GuidanceTooltipSchemaType.guidanceTooltip>;
  [IActionType.showAllGuidanceTooltipModifications]: undefined;
  [IActionType.hideAllGuidanceTooltipModifications]: undefined;
  [IActionType.openGuidanceTooltip]: Modification<GuidanceTooltipSchemaType.guidanceTooltip>;
  [IActionType.visualSearch]: CreateDataRuleFromElementPayload;
  [IActionType.showDemoModifiedNotification]: {
    userEmail: string;
  };
  [IActionType.togglePerformanceMeasuring]: {
    val: boolean;
    shouldStopOnLoad: boolean;
    stopOnSelectorXpath: string;
  };
  [IActionType.startPerformancePickSelectorMode]: undefined;
}

export type DatalessMessageNames<Message> = {
  [Key in keyof Message]: Message[Key] extends undefined ? Key : never;
}[keyof Message];

export type EditorSandBoxEvents = keyof WindowEventMap;

export interface CreateDataRuleFromElementPayload {
  htmlElementString: string;
  referer?: string;
}

export type AssistTypes =
  | 'fake_data'
  | 'fake_data_replace'
  | 'FakeDataReplaceAssist'
  | 'FNRAssist';

export const assistType: Record<string, AssistTypes> = {
  FNRAssist: 'FNRAssist',
  FAKE_DATA: 'fake_data',
  FAKE_DATA_REPLACE: 'fake_data_replace',
  FAKE_DATA_REPLACE_ASSIST: 'FakeDataReplaceAssist',
};

export type TableCraftObjectsStatus =
  | 'loading'
  | 'indexing'
  | 'success'
  | 'error';
